/**
 * Projected 5h / 7d usage for the Roost Seats, Accounts and Wallboard pages
 * (Bug 34656). Built from the dashboard's own inputs (`buildAccountSignals`)
 * and math (`projectWindow` -> `projectPace` -> `projectedPctAtWindowEnd`,
 * leveled by `actOnLevel`), so a page and the dashboard warning (Bug 34652)
 * give the same number for the same account and pace samples.
 *
 * A page with no pace samples gets the window-average projection, marked
 * "avg", so it never passes off a different number as the dashboard's.
 */
import type { AccountRollup } from '../models/flock-telemetry.models';
import { buildAccountSignals } from './attention-inputs.logic';
import {
  type Severity,
  type WindowProjection,
  projectWindow,
} from './attention.logic';
import type { PaceBasis, PaceSampleMap, WindowKey } from './pace.logic';

export interface ProjectedUsageVm {
  window: WindowKey;
  percent: number;
  /** "42%" */
  currentText: string;
  projectedPct: number | null;
  /** "88%", "88% avg" or "n/a" (see `projectedText`). */
  projectedText: string;
  basis: PaceBasis;
  /** Act-on level, the same rule as the dashboard warnings. */
  level: Severity | null;
}

export type WindowProjections = Partial<Record<WindowKey, ProjectedUsageVm>>;

/** Keyed by `projectionKey(accountUpn)`. */
export type AccountProjections = Map<string, WindowProjections>;

/** The key `accountProjections` writes: look a row up with the same key. */
export function projectionKey(accountUpn: string): string {
  return accountUpn.trim().toLowerCase();
}

function pctText(v: number): string {
  return `${Math.round(v)}%`;
}

/**
 * The projected-at-reset label: "n/a" with no projection, "P% avg" when it is
 * the window average (no usable recent burn), else "P%". A window already at
 * its limit reads as its current percent, unmarked.
 */
export function projectedText(
  p: Pick<WindowProjection, 'percent' | 'projectedPct' | 'basis'>
): string {
  if (p.projectedPct === null) return 'n/a';
  const avg = p.basis === 'window-average' && p.percent < 100;
  return `${pctText(p.projectedPct)}${avg ? ' avg' : ''}`;
}

/**
 * Every measured, live 5h / 7d window per account, projected exactly as the
 * dashboard does. A window the dashboard skips (estimated, or its reset has
 * passed) has no entry, which the pages render as n/a.
 */
export function accountProjections(
  rollups: AccountRollup[],
  samples: PaceSampleMap,
  nowMs: number
): AccountProjections {
  const out: AccountProjections = new Map();
  for (const a of buildAccountSignals(
    rollups,
    null,
    new Map(),
    samples,
    nowMs
  )) {
    const windows: WindowProjections = {};
    for (const w of a.windows) {
      const p = projectWindow(w, nowMs);
      windows[w.window] = {
        window: w.window,
        percent: p.percent,
        currentText: pctText(p.percent),
        projectedPct: p.projectedPct,
        projectedText: projectedText(p),
        basis: p.basis,
        level: p.level,
      };
    }
    out.set(projectionKey(a.accountUpn), windows);
  }
  return out;
}

const LEVEL_RANK = { crit: 2, warn: 1 } as const;

/**
 * One line for a seat card, "5h 42% → 60% avg · 7d 76% → 95% avg", with the
 * worse act-on level of the two for its highlight.
 */
export function projectedUsageLine(windows: WindowProjections | undefined): {
  text: string;
  level: Severity | null;
} {
  let level: Severity | null = null;
  const parts = (['5h', '7d'] as const).map((key) => {
    const w = windows?.[key];
    if (!w) return `${key} n/a`;
    if (w.level && (!level || LEVEL_RANK[w.level] > LEVEL_RANK[level]))
      level = w.level;
    return `${key} ${w.currentText} → ${w.projectedText}`;
  });
  return { text: parts.join(' · '), level };
}
